import { useCallback, useState } from 'react'

/**
 * `useState` backed by `localStorage` — the per-user persistence mechanism
 * for density/columns/saved-views in this slice (ListView §3.2's "per-user
 * persisted" requirement). There is no dedicated preferences API in the
 * IB-4 contract yet, so this is a deliberate, honest stand-in: it works and
 * survives reloads for the user on this device, but it is not the
 * server-side, cross-device preference record `docs/design-system.md` §2.1
 * describes for theme. Flagged in the UX report as a follow-up.
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : defaultValue
    } catch {
      return defaultValue
    }
  })

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState((prev) => {
        const resolved = typeof next === 'function' ? (next as (prev: T) => T)(prev) : next
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved))
        } catch {
          // Storage unavailable (private mode, quota) — state still updates in memory.
        }
        return resolved
      })
    },
    [key],
  )

  return [state, update]
}
